/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useRef } from "react";
import Config from "data/config";
import { Logger } from "cms/utils/log/logger";

/*
 - initially the first update is not blocked and will execute
 - the blocker ref is used to block a sequence of calls for a delay period
 - when it executes,
   - it will set the blocker to block further updates
   - it will start a timer that will eventually clear the blocker
 - if the blocker prevents the execution of an update, it will use a second timer to ensure a final update:
   - it first cancels any previous running (second) timer
   - it starts a new (second) timer
   - at the end of the (second) timer it then executes the update and clears the blocker
   - at this point both timers have ended and the blocker is cleared.
   - based on
   
        const debouncedAction = (fn, delay = 600) => {
            const delayed = setTimeout(() => {
                fn()
            }, delay)
            return () => clearTimeout(delayed)
        }

   
 */

// const defaultInterval = Config.throttleWait
const defaultEventDebounce = Config.eventDebounce;

const useThrottle = () => {
    const blocker = useRef(null);
    let delayed = null;
    
    const throttleFunction = useRef((
        func,
        // interval = defaultInterval,
        debounce = defaultEventDebounce
    ) => {
        
        // stop the previous final update, each time a new update arrives
        clearTimeout(delayed);
        
        // but add timer to call the incoming update (as a final update), if no further update supersede it.
        delayed = setTimeout(() => {
            
            if (Config.Logging.enableSavePipelineLog) {
                const updateTime = performance.now();
                Logger.debug(`useThrottle -> executed update after wait [${updateTime}]`);
            }
            
            // if the timer is allowed to complete the final update is executed
            func();
            
            // The blocker was likely already cleared 5ms ago, but let's make sure.
            blocker.current = null;
        }, debounce);
    });
    
    return throttleFunction.current;
};


useThrottle.displayName = "useThrottle";
export default useThrottle;
